import { cn } from "@tine/designsystem-utils";
import { type HTMLAttributes, useEffect, useLayoutEffect, useRef, useState } from "react";
import useWindowDimensions from "../../common/hooks/useWindowDimensions";
import Button from "../Button";

type GapNumber = 1 | 2 | 3 | 4;

type ShowMoreContainerProps = {
   rows?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;
   gap?: GapNumber;
} & HTMLAttributes<HTMLDivElement>;

const twGaps: Record<GapNumber, string> = {
   1: "tw-gap-1",
   2: "tw-gap-2",
   3: "tw-gap-3",
   4: "tw-gap-4"
};

const getLastBoundedChild = (container: HTMLElement, boundaryElement: HTMLElement) => {
   if (container && boundaryElement) {
      const { children } = container;
      let lastChild: HTMLElement | null = null;
      for (const child of children) {
         if (child instanceof HTMLElement) {
            const { y: childY, height: childHeight } = child.getBoundingClientRect();
            const { y: containerY, height: boundaryHeight } = boundaryElement.getBoundingClientRect();
            if (childY + childHeight <= containerY + boundaryHeight) {
               lastChild = child;
            } else {
               return lastChild;
            }
         }
      }
      return lastChild;
   }

   return null;
};

/* Show a "show all/show less" button when the number of rows exceeds the supplied value.
 children should be a list of elements that will be flex-wrapped in this container.
The row height is determined by the height of the first child element.  Make sure all of the 
children are the same height, otherwise the elements may be chopped in half. */

const ShowMoreContainer = ({ className = "", rows = 1, gap = 1, children, ...props }: ShowMoreContainerProps) => {
   const [expanded, setExpanded] = useState<boolean>(false);
   const [expandable, setExpandable] = useState<boolean>(false);
   const [rowHeight, setRowHeight] = useState<number>(0);
   const [containerHeight, setContainerHeight] = useState<number>(0);
   const [transitionTimeout, setTransitionTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
   const { width: windowWidth } = useWindowDimensions();
   const ref = useRef<HTMLDivElement>(null);
   const buttonRef = useRef<HTMLButtonElement>(null);
   const nbsp = String.fromCodePoint(0x00a0);
   const expandButtonText = expanded ? `Vis${nbsp}færre` : `Vis${nbsp}flere`;

   useEffect(() => {
      return () => {
         if (transitionTimeout) {
            clearTimeout(transitionTimeout);
         }
      };
   }, [transitionTimeout]);

   // Set the row height and container height based on the first child element
   // and check if the container is expandable based on the number of rows
   useLayoutEffect(() => {
      const { current: containerElement } = ref;
      if (containerElement) {
         const { height } = containerElement.getBoundingClientRect();
         const { firstElementChild } = containerElement;
         const { height: childHeight } = firstElementChild?.getBoundingClientRect() || { height: 0 };

         setRowHeight(childHeight);

         setContainerHeight(height);

         const newExpandable = height > (childHeight + 4 * gap) * rows;
         if (expandable && !newExpandable) {
            setExpanded(false);
         }
         setExpandable(newExpandable);
         if (!newExpandable) {
            for (const child of containerElement.children) {
               if (child instanceof HTMLButtonElement) {
                  child.disabled = false;
               }
            }
         }
      }
   }, [children, ref, windowWidth, gap, rows]);

   useLayoutEffect(() => {
      const { current: containerElement } = ref;
      const { current: buttonElement } = buttonRef;
      if (expandable && containerElement && buttonElement && !transitionTimeout) {
         const { parentElement: wrapperElement } = containerElement;
         if (wrapperElement) {
            if (!expanded) {
               const lastBoundedChild = getLastBoundedChild(containerElement, wrapperElement);
               if (lastBoundedChild && lastBoundedChild !== buttonElement) {
                  containerElement?.insertBefore(buttonElement, lastBoundedChild);
                  const newLastBoundedChild = getLastBoundedChild(containerElement, wrapperElement);
                  if (newLastBoundedChild && newLastBoundedChild !== buttonElement) {
                     containerElement?.insertBefore(newLastBoundedChild, buttonElement);
                  }
               }
            } else {
               containerElement?.appendChild(buttonElement);
            }
            const children = Array.from(containerElement.children);
            const buttonIndex = children.indexOf(buttonElement);
            for (const child of children) {
               if (child instanceof HTMLButtonElement) {
                  const childIndex = children.indexOf(child);
                  if (childIndex < buttonIndex) {
                     child.disabled = false;
                  } else if (childIndex > buttonIndex) {
                     child.disabled = true;
                  }
               }
            }
         }
      }
   }, [windowWidth, expanded, expandable, containerHeight, rowHeight, transitionTimeout]);

   const handleExpandClick = () => {
      setTransitionTimeout(
         setTimeout(() => {
            setTransitionTimeout(null);
         }, 300)
      );
      setExpanded(!expanded);
   };

   const wrapperStyle = cn("tw-overflow-hidden tw-p-[2px]", {
      "tw-transition-height tw-duration-300 tw-ease-in-out": transitionTimeout
   });
   const childrenStyle = `tw-flex tw-flex-row tw-flex-wrap ${twGaps[gap]} tw-m-0 tw-p-0`;

   const buttonStyle = cn("tw-w-[68px] tw-border", { "tw-invisible": transitionTimeout });

   const cssHeight = `calc(${rowHeight * rows}px + 4px + ${((rows - 1) * gap) / 4}rem)`;

   const wrapperAdditionalProps =
      !expanded && expandable && rowHeight && containerHeight
         ? { style: { height: cssHeight } }
         : { style: { height: `calc(${containerHeight}px + 4px)` } };

   return (
      <div {...props}>
         <div {...wrapperAdditionalProps} className={wrapperStyle}>
            <div className={childrenStyle} ref={ref}>
               {children}
               {(expandable || expanded) && (
                  <Button className={buttonStyle} ref={buttonRef} onClick={handleExpandClick} variant="tertiary">
                     {expandButtonText}
                  </Button>
               )}
            </div>
         </div>
      </div>
   );
};

export default ShowMoreContainer;
